import "./BrandFont.scss";
import {FontDto, UploadedFontDto} from "../../domain/BrandDataDto";
import {useEffect, useRef, useState} from "react";
import {Button, Dropdown, Input} from "antd";
import {TimberFont} from "../../generated/dashboard-client";
import BoldIcon from "../icons/BoldIcon";
import ItalicIcon from "../icons/ItalicIcon";
import UnderlineIcon from "../icons/UnderlineIcon";
import {EditOutlined} from "@ant-design/icons";
import {useOnClickOutside} from 'usehooks-ts';

export enum BrandFontType {
  Header,
  SubHeader,
  BodyText,
}

interface DropdownFontDto {
  key: string;
  name: string;
  label: string;
  visible: boolean;
  type: string;
}

export interface BrandFontProps {
  fonts: TimberFont[];
  uploadedFonts: UploadedFontDto[];
  type: BrandFontType;
  label: string;
  font: FontDto;
  onChange: (font: FontDto) => void;
}

function getDefaultStyles(type: BrandFontType) {
  if (type === BrandFontType.Header) {
    return {
      fontSize: `${getDefaultFontSize(type)}px`,
      fontWeight: "bold",
    };
  }
  return {
    fontSize: `${getDefaultFontSize(type)}px`,
  };
}

function getDefaultFontSize(type: BrandFontType) {
  if (type === BrandFontType.Header) return "40";
  if (type === BrandFontType.SubHeader) return "30";
  if (type === BrandFontType.BodyText) return "20";
}

export default function BrandFont({
                                    label,
                                    font,
                                    type,
                                    fonts,
                                    uploadedFonts,
                                    onChange,
                                  }: BrandFontProps) {
  const ref = useRef(null);
  const [allFonts, setAllFonts] = useState([] as DropdownFontDto[]);
  const [isEditMode, setEditMode] = useState(false);
  const [fontSearch, setFontSearch] = useState("");
  const [fontName, setFontName] = useState("");
  const [uploadedFontsVisible, setUploadedFontsVisible] = useState(true);
  const [isFontOptionsVisible, setFontOptionsVisible] = useState(false);

  useEffect(() => {
    if (font?.name) {
      setFontSearch(font?.name);
    } else {
      setFontSearch("");
    }
  }, [font?.name])

  useEffect(() => {
    if (font?.fontName) {
      setFontName(font.fontName);
    } else if (font) {
      allFonts.forEach((it) => {
        if (it.label === font?.name) {
          setFontName(it.name);
        }
      })
    }
  }, [font, allFonts])

  useEffect(() => {
    let fontsList = [] as DropdownFontDto[];
    if (uploadedFonts.length === 0) {
      setUploadedFontsVisible(false);
    } else {
      uploadedFonts.forEach((font) => {
        fontsList.push({
          key: font.id!,
          name: font.name!,
          label: nameWithoutExtension(font.name!) as string,
          visible: true,
          type: 'uploaded'
        })
      });
    }

    fonts.forEach((font) => {
      fontsList.push({
        key: font.cls!,
        name: font.name!,
        label: font.label!,
        visible: font.visible!,
        type: 'default'
      });
    });

    setAllFonts(fontsList);
  }, [fonts, uploadedFonts])

  const handleClickOutside = (ev: MouseEvent) => {
    if (ev.target) {
      let element = ev.target as HTMLElement;
      if (element.classList.contains('ant-select-item-option') || element.parentElement?.classList.contains('ant-select-item-option') || element.parentElement?.parentElement?.classList.contains('ant-select-item-option')) {
        return;
      }
    }
    if (isEditMode) {
      setEditMode(false);
    }
  }

  useOnClickOutside(ref, (ev) => handleClickOutside(ev as MouseEvent));

  function onSearch(txt: string) {
    setFontSearch(txt);
    txt = txt.toLowerCase();
    allFonts.forEach(
      (it) => (it.visible = it.name!.toLowerCase().indexOf(txt) > -1)
    );
    setUploadedFontsVisible(allFonts.filter(function (it) {
      return it.type === 'uploaded' && it.visible;
    }).length > 0);
    setAllFonts([...allFonts]);
  }

  function onNameSelect(newFont: string) {
    setFontOptionsVisible(false);
    const found = allFonts.find((it) => it.label === newFont);
    if (!found) return;
    if (!font) {
      let newFont = {} as FontDto;
      newFont["name"] = found.label!;
      newFont["fontName"] = found.name!;
      onChange({
        ...newFont
      });
    } else {
      font["name"] = found.label!;
      font["fontName"] = found.name!;
      onChange({
        ...font
      });
    }
  }

  function toggleEdit() {
    setEditMode(!isEditMode);
  }

  function toggleProperty(key: "bold" | "italic" | "underline") {
    setFontOptionsVisible(false);
    font[key] = !font[key];
    onChange({...font});
  }

  function nameWithoutExtension(name: String) {
    let fileExtPos = name.lastIndexOf(".");
    if (fileExtPos >= 0) {
      return name.substring(0, fileExtPos);
    } else {
      return name;
    }
  }

  return (
    <div className={`brand-font ${label.toLowerCase()}`}>
      {!isEditMode && (
        <>
          {!font?.name && (
            <label
              className={"plc"}
              style={getDefaultStyles(type)}
              onClick={toggleEdit}
            >
              Set {label} font
            </label>
          )}
          {font?.name && (
            <label className={"lbl"}>
              <div
                style={{
                  fontFamily: `'${fontName}'`,
                  fontWeight: font?.bold ? "bold" : "initial",
                  fontStyle: font?.italic ? "italic" : "normal",
                  textDecoration: font?.underline ? "underline" : "none",
                  fontSize: `${getDefaultFontSize(type)}px`
                }}
              >
                {label}, {nameWithoutExtension(font?.name)}, {getDefaultFontSize(type)}
              </div>
              <Button type={"link"} onClick={toggleEdit}>
                <EditOutlined/>
              </Button>
            </label>
          )}
        </>
      )}

      {isEditMode && (
        <div className={"widget"} ref={ref}>
          <Dropdown
            visible={isFontOptionsVisible}
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            overlayClassName={"font-options"}
            overlay={
              <div>
                {uploadedFontsVisible &&
                    <div className={"uploaded-fonts-lbl"}>Uploaded Fonts</div>}
                {allFonts
                  .filter((it) => it.visible && it.type === "uploaded")
                  .map((it) => {
                    return (<div key={it.key} className={"font-option"} data-value={it.name} onClick={() => onNameSelect(it.label)}>
                      <span style={{fontFamily: `'${it.name}'`}}>{it.label}</span>
                    </div>)
                  })}
                {uploadedFontsVisible &&
                    <div className={"separator"}/>}
                {allFonts
                  .filter((it) => it.visible && it.type === "default")
                  .map((it) => {
                    return (<div key={it.key} className={"font-option"} data-value={it.label} onClick={() => onNameSelect(it.label)}>
                      <span style={{fontFamily: `'${it.name}'`}}>{it.label}</span>
                    </div>)
                  })}
              </div>
            }
          >
            <Input
              size={"large"}
              className={"font-val"}
              placeholder={"Enter a font name"}
              onChange={(ev) => onSearch(ev.target.value)}
              value={nameWithoutExtension(fontSearch) as string}
              onFocus={() => setFontOptionsVisible(true)}
            />
          </Dropdown>

          <ul>
            <li>
              <Button
                className={font?.bold ? "act" : ""}
                onClick={() => toggleProperty("bold")}
              >
                <BoldIcon/>
              </Button>
            </li>
            <li>
              <Button
                className={font?.italic ? "act" : ""}
                onClick={() => toggleProperty("italic")}
              >
                <ItalicIcon/>
              </Button>
            </li>
            <li>
              <Button
                className={font?.underline ? "act" : ""}
                onClick={() => toggleProperty("underline")}
              >
                <UnderlineIcon/>
              </Button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
